/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { getRequestOptions } from '../helpers/Fetchwrapper';
import { Col, Row, Table } from 'react-bootstrap-v5';
import { Link, useParams } from 'react-router-dom';
import { KTSVG } from '../../_metronic/helpers';

interface QuotationRow {
    id_sales_quotation_rows: number;
    id_products: number;
    unit_rate: number;
    transportation_cost: number;
    quantity: number;
    original_rate: number;
    discount_percent: number;
    remarks: string;
    delivery_location: string;
    product?: {
        product_name: string;
        packing: string;
        item?: {
            unit?: {
                unit_name: string;
            };
        };
    };
    alternate_name?: {
        alternate_name: string;
    };
}

interface QuotationTerm {
    id_sales_quotation_terms: number;
    clause?: string;
    terms_clause?: string;
}

interface QuotationInfo {
    id_sales_quotations: number;
    id_projects: number;
    id_leads: number;
    quotation_date: string;
    reference_number: string;
    quotation_text: string;
    quotation_footer: string;
    project?: {
        project_name: string;
    };
    lead?: {
        lead_name: string;
    };
    rows: QuotationRow[];
    terms: QuotationTerm[];
}

interface RouteParams {
    idSalesQuotations: string;
}

const numberFormat = (value: any) => {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
        return value ?? '';
    }
    return parsed.toLocaleString('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const getProductName = (row: QuotationRow) => {
    const alternateName = row.alternate_name?.alternate_name;
    const productName = row.product?.product_name ?? '';
    const packing = row.product?.packing ?? '';
    const unitName = row.product?.item?.unit?.unit_name ?? '';

    const name = alternateName && alternateName.trim() !== '' ? alternateName : productName;
    const packLabel = packing !== '' && unitName !== '' ? ` (${packing} ${unitName})` : '';

    return `${name}${packLabel}`;
};

const getRowAmount = (row: QuotationRow) => {
    const quantity = parseFloat(String(row.quantity ?? 0)) || 0;
    const rate = parseFloat(String(row.unit_rate ?? 0)) || 0;
    const transportationCost = parseFloat(String(row.transportation_cost ?? 0)) || 0;

    return quantity * (rate + transportationCost);
};

export default function SalesQuotationDetails() {
    const { idSalesQuotations }: RouteParams = useParams();
    const [quotationInfo, setQuotationInfo] = useState<QuotationInfo | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (idSalesQuotations) {
            getQuotationInfo();
        }
    }, [idSalesQuotations]);

    const getQuotationInfo = () => {
        setLoading(true);
        fetch(`${process.env.REACT_APP_API_URL}quotation/${idSalesQuotations}`, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                setQuotationInfo(resp.quotation ?? null);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const totalAmount = quotationInfo?.rows?.reduce((sum, row) => sum + getRowAmount(row), 0) ?? 0;

    if (loading) {
        return <div className="card card-custom p-10">Loading quotation details...</div>;
    }

    if (!quotationInfo) {
        return <div className="card card-custom p-10">Quotation not found.</div>;
    }

    return (
        <>
            <div className="card card-custom mb-5">
                <div className="card-header">
                    <div className="card-title">
                        <KTSVG path="/media/svg/icons/Layout/Layout-grid.svg" className="svg-icon-1 svg-icon-primary" />
                        <span className="text fw-bolder" style={{ marginLeft: '10px' }}>
                            Sales Quotation Details
                        </span>
                    </div>
                    <div className="card-toolbar">
                        <Link to="/sales-quotations" className="btn btn-sm btn-light-primary me-2">
                            Back to List
                        </Link>
                        <Link
                            to={`/sales-quotation-create?idSalesQuotations=${quotationInfo.id_sales_quotations}`}
                            className="btn btn-sm btn-light-primary me-2"
                        >
                            <KTSVG path="/media/svg/icons/Design/Edit.svg" className="svg-icon-5 svg-icon" /> Edit
                        </Link>
                        <a
                            className="btn btn-sm btn-light-primary"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${process.env.REACT_APP_BACKEND}quotationDownload/${quotationInfo.id_sales_quotations}`}
                        >
                            <KTSVG path="/media/svg/icons/Files/Downloads-folder.svg" className="svg-icon-5 svg-icon" /> Download
                        </a>
                    </div>
                </div>
            </div>

            <div className="card card-custom mb-5">
                <div className="card-body">
                    <Row className="mb-4">
                        <Col md={6}>
                            <div><strong>Project:</strong> {quotationInfo.project?.project_name ?? ''}</div>
                            <div><strong>Lead:</strong> {quotationInfo.lead?.lead_name ?? ''}</div>
                        </Col>
                        <Col md={6}>
                            <div><strong>Date:</strong> {quotationInfo.quotation_date ?? ''}</div>
                            <div><strong>Reference No:</strong> {quotationInfo.reference_number ?? ''}</div>
                        </Col>
                    </Row>

                    <div className="mb-5">
                        <h5 className="mb-3">Quotation Header</h5>
                        <div
                            className="border rounded p-4 bg-light"
                            dangerouslySetInnerHTML={{ __html: quotationInfo.quotation_text ?? '' }}
                        />
                    </div>

                    <h5 className="mb-3">Products</h5>
                    <Table responsive bordered hover size="sm">
                        <thead>
                            <tr>
                                <th className="text-center">SL</th>
                                <th>Product</th>
                                <th>Delivery Location</th>
                                <th className="text-end">Rate (Per Unit)</th>
                                <th className="text-end">Transportation Cost</th>
                                <th className="text-end">Effective Rate</th>
                                <th className="text-end">Quantity</th>
                                <th className="text-end">Amount</th>
                                <th className="text-end">Discount (%)</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotationInfo.rows?.map((row, index) => {
                                const rate = parseFloat(String(row.unit_rate ?? 0)) || 0;
                                const transportationCost = parseFloat(String(row.transportation_cost ?? 0)) || 0;
                                const effectiveRate = rate + transportationCost;

                                return (
                                    <tr key={row.id_sales_quotation_rows ?? index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>{getProductName(row)}</td>
                                        <td>{row.delivery_location ?? ''}</td>
                                        <td className="text-end">{numberFormat(rate)}</td>
                                        <td className="text-end">{numberFormat(transportationCost)}</td>
                                        <td className="text-end">{numberFormat(effectiveRate)}</td>
                                        <td className="text-end">{numberFormat(row.quantity)}</td>
                                        <td className="text-end">{numberFormat(getRowAmount(row))}</td>
                                        <td className="text-end">{numberFormat(row.discount_percent)}</td>
                                        <td>{row.remarks ?? ''}</td>
                                    </tr>
                                );
                            })}
                            <tr>
                                <td colSpan={7} className="text-end"><strong>Total Amount</strong></td>
                                <td className="text-end"><strong>{numberFormat(totalAmount)}</strong></td>
                                <td colSpan={2}></td>
                            </tr>
                        </tbody>
                    </Table>

                    <h5 className="mb-3 mt-5">Business Terms & Conditions</h5>
                    <Table responsive bordered hover size="sm">
                        <thead>
                            <tr>
                                <th className="text-center" style={{ width: '10%' }}>SL</th>
                                <th>Clause</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(quotationInfo.terms ?? []).map((term, index) => (
                                <tr key={term.id_sales_quotation_terms ?? index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>{term.clause ?? term.terms_clause ?? ''}</td>
                                </tr>
                            ))}
                            {(quotationInfo.terms ?? []).length === 0 && (
                                <tr>
                                    <td colSpan={2} className="text-center">No terms added</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <div className="mt-5">
                        <h5 className="mb-3">Quotation Footer</h5>
                        <div
                            className="border rounded p-4 bg-light"
                            dangerouslySetInnerHTML={{ __html: quotationInfo.quotation_footer ?? '' }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
